/**
 * Stage selector for a remote start.
 *
 * `phone.start_recording` with no `stage_index` binds to whatever stage the
 * driver targeted in the cockpit — which is null on a phone that has picked
 * nothing, the default state after every geofence change, and the start is
 * then refused with "No eligible stage is targeted". Naming a stage here
 * retargets the phone instead of depending on the driver.
 *
 * A named stage the phone rejects (unconfigured, not eligible yet, or another
 * stage already recording) fails the command with that reason and leaves
 * nothing behind — every stage guard runs before the phone reserves a start.
 * So the list offers every configured stage rather than predicting which ones
 * will take; the operator reads the refusal off the command row.
 */

import React, { useEffect, useMemo, useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { buildStageViews, stageLabel } from '../pages/admin/stages/stageModel';
import { useCurrentEvent } from '../state/currentEvent';

/** Sentinel for "send no stage_index" — see startRecordingPayload. */
export const DRIVER_CHOICE = 'driver';

export interface StartStageOption {
  index: number;
  /** SS1 / H2 — from stageModel.stageLabel. */
  label: string;
  /** '' for a stage that has gates but no metadata. */
  name: string;
}

/**
 * Start-command payload for a picker choice. An empty payload is meaningful:
 * it leaves stage selection to the phone.
 */
export function startRecordingPayload(choice: string): Record<string, number> {
  // Digits-only rather than a numeric parse: Number('') is 0, so anything but
  // a real index has to fall back to the driver's pick instead of silently
  // becoming stage 0.
  if (!/^\d+$/.test(choice)) return {};
  return { stage_index: Number(choice) };
}

/**
 * Picker state for the current event's stages. `startChoice` is resolved
 * against the live stage list, so a stage edited out from under the picker
 * falls back to the driver's pick instead of arming an index that no longer
 * exists — read it, never the raw picker value.
 *
 * A stage index only means something for one event (and, in DeviceDetail,
 * one car), so the choice resets when the event or `resetKey` changes —
 * otherwise the picker silently arms the next car on the previous one's stage.
 */
export function useStartStage(resetKey?: string) {
  const { currentEvent } = useCurrentEvent();
  const [startStage, setStartStage] = useState<string>(DRIVER_CHOICE);
  const stageOptions = useMemo<StartStageOption[]>(
    () =>
      currentEvent
        ? buildStageViews(currentEvent).map((v) => ({
            index: v.index,
            label: stageLabel(v.index, v.type),
            name: v.name,
          }))
        : [],
    [currentEvent],
  );
  const startStageLabel = stageOptions.find((s) => String(s.index) === startStage)?.label;
  const startChoice = startStageLabel ? startStage : DRIVER_CHOICE;
  useEffect(() => setStartStage(DRIVER_CHOICE), [resetKey, currentEvent?.id]);
  return { stageOptions, startChoice, startStageLabel, setStartStage };
}

interface StartStagePickerProps {
  stages: StartStageOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const StartStagePicker: React.FC<StartStagePickerProps> = ({
  stages,
  value,
  onChange,
  disabled,
}) => (
  <Select value={value} disabled={disabled} onValueChange={onChange}>
    <SelectTrigger className="w-44" aria-label="Stage to record">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={DRIVER_CHOICE}>Driver&apos;s stage</SelectItem>
      {stages.map((s) => (
        <SelectItem key={s.index} value={String(s.index)}>
          {s.label}
          {s.name ? ` · ${s.name}` : ''}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default StartStagePicker;
